import { ClipBoardTooltip } from "@/components/ClipBoardTooltip.tsx";
import { WButton } from "@/components/button/WButton.tsx";
import { Loading } from "@/components/loading/Loading.tsx";
import { getResourceById } from "@/modules/api/resourceTypeException.tsx";
import { useStandardMutation } from "@/modules/api/useStandardMutation.ts";
import { MachineSizeInfo } from "@/modules/compute/MachineSizeInfo.tsx";
import { VmImageIcon } from "@/modules/compute/VmImageIcon.tsx";
import { allAssignedVmsQueryAtom } from "@/modules/compute/joinAssignedQuery.ts";
import { OsTitle } from "@/modules/compute/os/OsInfo.tsx";
import { extractOsFields } from "@/modules/compute/os/os.ts";
import { useOsImage } from "@/modules/compute/os/useOsImage.ts";
import type { VmCreateInputs } from "@/modules/compute/vmCreateViewModel.tsx";
import { getReplicaByUuid } from "@/modules/compute/vmEntityUtils.ts";
import { stopVmMutation } from "@/modules/compute/vmQuery.ts";
import type { MachineCommonFields, VmStoragePriceFields } from "@/modules/pricing/resourcePricing.ts";
import { useSuspenseQueryAtom } from "@/utils/query/useSuspenseQueryAtom.ts";
import { Trans } from "@lingui/react/macro";
import type {
    VirtualMachine,
    VirtualMachineStorage,
    VirtualMachineStorageReplica,
} from "@warrenio/api-spec/spec.oats.gen";
import { notNull } from "@warrenio/utils/notNull";
import type { ReactNode } from "react";

/** Common VM fields shared between user & admin side */
export interface VmCommonFields extends MachineCommonFields {
    name: VirtualMachine["name"];
    uuid: VirtualMachine["uuid"];

    os_name: VirtualMachine["os_name"];
    os_version: VirtualMachine["os_version"];
    designated_pool_uuid?: VirtualMachine["designated_pool_uuid"];

    storage: StorageCommonFields[];
}

export interface StorageCommonFields extends VmStoragePriceFields {
    uuid: VirtualMachineStorage["uuid"];
}

export function ModalHeaderBlock({
    vm,
    children,
    replica,
    showSize,
}: {
    vm: VmCommonFields;
    children?: ReactNode;
    replica?: VirtualMachineStorageReplica;
    showSize: boolean;
}) {
    const selectedOs = extractOsFields(vm);
    const { image } = useOsImage(selectedOs);

    const { name, uuid } = vm;

    return (
        <div className="hidden md:flex gap-3 items-center justify-between lightBlock">
            <VmImageIcon image={image} className="color-primary size-2.5rem" />
            <div className="flex-grow VStack gap-0.5 children:min-h-1.5em">
                <div>
                    {replica ? (
                        <ClipBoardTooltip text={uuid}>
                            <b>{replica.uuid}</b>
                        </ClipBoardTooltip>
                    ) : (
                        <>
                            <b>{name}</b> - <ClipBoardTooltip>{uuid}</ClipBoardTooltip>
                        </>
                    )}
                </div>
                <div className="color-muted">
                    {replica ? (
                        <>
                            <Trans>Created from</Trans> <b>{name}</b>
                        </>
                    ) : (
                        <OsTitle os={selectedOs} />
                    )}
                </div>
                {showSize && <MachineSizeInfo vm={vm} />}
            </div>

            {children}
        </div>
    );
}

export function VmCloneHeaderBlock({ inputs, children }: { inputs?: Partial<VmCreateInputs>; children?: ReactNode }) {
    const vms = useSuspenseQueryAtom(allAssignedVmsQueryAtom);

    if (!inputs) {
        return <Loading />;
    }

    const { source_replica, source_uuid } = inputs;
    if (!source_replica) {
        return null;
    }

    const vm = getResourceById(vms, notNull(source_uuid, "source_uuid"), "virtual_machine");
    const replica = getReplicaByUuid(vm, source_replica);

    return (
        <ModalHeaderBlock vm={vm} replica={replica} showSize>
            {children}
        </ModalHeaderBlock>
    );
}

export function VmStopModalHeaderBlock({
    vm,
    showStopButton = false,
}: {
    vm: VmCommonFields;
    showStopButton?: boolean;
}) {
    const stopMutation = useStandardMutation(stopVmMutation);
    const { uuid, location, status } = vm;
    return (
        <ModalHeaderBlock vm={vm} showSize>
            {showStopButton && (
                <WButton
                    isDisabled={status === "stopped"}
                    icon="jp-icon-stop"
                    variant="ghost"
                    color="primary"
                    action={async () => {
                        await stopMutation.mutateAsync({ uuid, location, force: false });
                    }}
                >
                    {status === "running" ? <Trans>Stop</Trans> : <Trans>Stopped</Trans>}
                </WButton>
            )}
        </ModalHeaderBlock>
    );
}
